import { MouseEventHandler } from "react";
import { SvgIconProps } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import BlockIcon from "@mui/icons-material/Block";

export type BigRoundButtonIconSize = SvgIconProps["fontSize"] | "xs" | "sm" | "lg" | "xl";

interface Props {
  backgroundColor?: string;
  icon?: string;
  iconColor?: string;
  tooltip?: string;
  onClick?: MouseEventHandler;
  iconSize?: BigRoundButtonIconSize;
  buttonHeight?: string;
  buttonWidth?: string;
  borderRadius?: string;
  disabled?: boolean;
  className?: string;
  dataTestId?: string;
}

const iconMapping: Record<string, React.ElementType> = {
  arrowLeft: ArrowBackIcon,
  arrowRight: ArrowForwardIcon,
  checkmark: CheckIcon,
  pen: EditIcon,
  calendarTimes: EventBusyIcon,
  ban: BlockIcon,
};

const DEFAULT_ICON_SIZE = "1.33rem";

const getIconSx = (
  fontSize: string,
  iconColor: string = "#FFF",
): SvgIconProps["sx"] => ({
  color: iconColor,
  fontSize,
  display: "inline-block",
  overflow: "visible",
  boxSizing: "content-box",
  height: "1em",
  lineHeight: 0.05,
  verticalAlign: "-0.075em",
  "& path, & circle, & rect, & polygon, & line": {
    stroke: "currentColor",
    strokeWidth: 0.6,
    strokeLinejoin: "round",
    paintOrder: "stroke fill",
  },
});

const resolveIconSize = (size?: BigRoundButtonIconSize): string => {
  if (!size) {
    return DEFAULT_ICON_SIZE;
  }

  const legacySizeMap: Record<string, string> = {
    xs: "0.75rem",
    sm: "0.875rem",
    lg: "1.33rem",
    xl: "1.5rem",
    small: "1.25rem",
    medium: "1.5rem",
    large: "1.33rem",
    inherit: "inherit",
  };

  if (typeof size === "string" && size in legacySizeMap) {
    return legacySizeMap[size];
  }

  return size;
};

export default function BigRoundButton(props: Props) {
  let backgroundColor = "#FF6428";

  if (props.backgroundColor != undefined) {
    backgroundColor = props.backgroundColor;
  }

  let buttonHeight = "48px";
  let buttonWidth = "48px";
  if (props.buttonHeight) {
    buttonHeight = props.buttonHeight;
  }
  if (props.buttonWidth) {
    buttonWidth = props.buttonWidth;
  }

  let borderRadius = "50%";
  if (props.borderRadius) {
    borderRadius = props.borderRadius;
  }

  const buttonStyle = {
    backgroundColor: backgroundColor,
    width: buttonWidth,
    height: buttonHeight,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "11px 13px",
    borderRadius: borderRadius,
    cursor: props.disabled ? "not-allowed" : "pointer",
    opacity: props.disabled ? 0.5 : 1,
  } as React.CSSProperties;

  let iconName = "arrowRight";
  if (props.icon && props.icon in iconMapping) {
    iconName = props.icon;
  }

  const IconElement = iconMapping[iconName];

  return (
    <div
      data-testid={props.dataTestId}
      title={props.tooltip}
      className={props.className}
      style={{ cursor: props.disabled ? "not-allowed" : "pointer" }}
      onClick={props.disabled ? undefined : props.onClick}
      role="button"
      aria-label={props.tooltip}
      aria-disabled={props.disabled}
    >
      <div style={buttonStyle}>
        <IconElement
          sx={getIconSx(resolveIconSize(props.iconSize), props.iconColor)}
          data-testid="bigRoundButtonIcon"
        />
      </div>
    </div>
  );
}
